import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, XCircle, Clock, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface SuspensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: 'suspended' | 'expired' | 'trial_expired';
}

const SuspensionModal: React.FC<SuspensionModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type
}) => {
  const { theme } = useTheme();

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'suspended':
        return <XCircle className="w-16 h-16 text-red-500" />;
      case 'expired':
        return <AlertTriangle className="w-16 h-16 text-orange-500" />;
      case 'trial_expired':
        return <Clock className="w-16 h-16 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-16 h-16 text-red-500" />;
    }
  };

  const getBgColor = () => {
    switch (type) {
      case 'suspended':
        return theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50';
      case 'expired':
        return theme === 'dark' ? 'bg-orange-900/20' : 'bg-orange-50';
      case 'trial_expired':
        return theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-50';
      default:
        return theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50';
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto animate-fadeIn">
      <div className="flex items-end sm:items-center justify-center min-h-screen px-4 sm:py-6">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className={`relative w-full max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl transition-all animate-scaleIn ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* Close button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 p-2 rounded-lg transition-colors touch-manipulation ${
              theme === 'dark'
                ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-300'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-6 sm:p-8 text-center">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className={`p-3 sm:p-4 rounded-full ${getBgColor()}`}>
                {getIcon()}
              </div>
            </div>

            {/* Title */}
            <h2 className={`text-xl sm:text-2xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {title}
            </h2>

            {/* Message */}
            <p className={`text-sm sm:text-base mb-6 leading-relaxed ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {message}
            </p>

            {/* Contact Support Message */}
            <div className={`p-4 rounded-xl mb-6 ${
              theme === 'dark' ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-xs sm:text-sm ${
                theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
              }`}>
                <strong>Need Help?</strong>
                <br />
                Please contact the administrator to resolve this issue and regain access to your account.
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-full px-6 py-3 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors touch-manipulation text-sm sm:text-base"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SuspensionModal;
